'use client';

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslations } from '@fshr/context/i18n-context';
import { imgOpt } from '@fshr/shared';
import { cn } from '@fshr/shared-react';
import AnchorLink from '../anchor-link';
import BevSafeText from '../bev-safe-text';
import Icon, { IconChevronLightLeft, IconChevronLightRight } from '../icon';
import Image from '../image';
import RichTextTitle from '../rich-text-title';
import YouTubeEmbed from '../youtube-embed';
import type { InlineCarouselProps, InlineCarouselSlide } from './types';
import useDragToScroll from '../carousel/use-drag-to-scroll';

const arrowButtonClass =
	'flex h-500 w-500 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0px_5px_7.5px_rgba(3,3,3,0.12)] transition-transform duration-300 hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2';

function extractYouTubeId(value?: string): string | undefined {
	if (!value) return undefined;
	if (/^[A-Za-z0-9_-]{11}$/.test(value)) return value;
	const match = value.match(
		/(?:youtube(?:-nocookie)?\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([A-Za-z0-9_-]{11})/
	);
	return match?.[1];
}

function InlineCarouselCard({
	slide,
	isActive,
}: {
	slide: InlineCarouselSlide;
	isActive: boolean;
}): ReactElement {
	const translate = useTranslations();
	const videoRef = useRef<HTMLVideoElement>(null);
	const [videoFailed, setVideoFailed] = useState(false);
	const [embedFailed, setEmbedFailed] = useState(false);
	const [isPlaying, setIsPlaying] = useState(slide.videoSource === 'dam');

	const youtubeId = extractYouTubeId(slide.videoId);

	const hasEmbed = slide.videoSource === 'youtube' && Boolean(youtubeId) && !embedFailed;
	const hasVideo = slide.videoSource === 'dam' && Boolean(slide.videoUrl) && !videoFailed;

	const onEmbedUnavailable = useCallback(() => setEmbedFailed(true), []);

	const posterSrc = slide.image?.defaultImage?.path
		? imgOpt({ path: slide.image.defaultImage.path, cropping: slide.image.defaultImage.cropping })
		: undefined;

	useEffect(() => {
		const video = videoRef.current;
		if (!video || !hasVideo) return;

		if (isActive && isPlaying) {
			video.play().catch(() => setVideoFailed(true));
		} else {
			video.pause();
		}
	}, [hasVideo, isActive, isPlaying]);

	useEffect(() => {
		const video = videoRef.current;
		if (!video || isActive) return;
		video.currentTime = 0;
	}, [isActive]);

	const showCta = Boolean(slide.ctaLabel && slide.ctaHref);

	return (
		<li className="w-[var(--card-w)] max-w-full shrink-0 snap-center" aria-hidden={!isActive}>
			<div className="relative aspect-[16/9] w-full overflow-hidden">
				{slide.image && (
					<Image
						roughSize="l"
						imagePrimitive={slide.image}
						className={cn(
							'absolute inset-0 h-full w-full object-cover',
							slide.mobileImage && 'hidden md:block'
						)}
					/>
				)}
				{slide.mobileImage && (
					<Image
						roughSize="l"
						imagePrimitive={slide.mobileImage}
						className="absolute inset-0 h-full w-full object-cover md:hidden"
					/>
				)}

				{hasVideo && (
					<video
						ref={videoRef}
						className="absolute inset-0 h-full w-full object-cover"
						poster={posterSrc}
						muted
						loop
						playsInline
						preload="metadata"
						aria-label={slide.videoAccessibilityText}
						onError={() => setVideoFailed(true)}
					>
						<source src={slide.videoUrl} type="video/mp4" />
					</video>
				)}

				{/* Kept mounted and driven by shouldPlay. Unmounting it on pause tore the player
				    down mid-load and took the component with it.
				    interactive=true gives the embed YouTube's own native play/pause controls, so
				    playback can start without ever touching our isPlaying state (which has no
				    button for this case anyway — that toggle is dam-video only). shouldPlay must
				    therefore track isActive alone: it's what forces a pause the moment the slide
				    goes inactive, regardless of how playback was started. */}
				{hasEmbed && youtubeId && (
					<YouTubeEmbed
						videoId={youtubeId}
						title={slide.title ?? ''}
						description={slide.videoAccessibilityText}
						shouldPlay={isActive}
						interactive
						onUnavailable={onEmbedUnavailable}
					/>
				)}

				{hasVideo && (
					<button
						type="button"
						onClick={() => setIsPlaying(playing => !playing)}
						tabIndex={isActive ? 0 : -1}
						aria-label={isPlaying ? translate('Pause video') : translate('Play video')}
						className="absolute bottom-[15px] left-[15px] z-20 flex size-200 shrink-0 items-center justify-center rounded-full bg-img-overlay-40 text-text-on-image-primary transition-colors hover:bg-img-overlay-60 focus-visible:outline-2 focus-visible:outline-offset-2 md:bottom-[20px] md:left-[20px]"
					>
						<Icon
							icon={isPlaying ? 'pause_button_large' : 'play_button_large'}
							className="text-[20px]"
						/>
					</button>
				)}
			</div>

			{isActive && (
				// Figma (Wide Grid: Inline Carousel, PLP file) has two different
				// gaps here, not one flat value: title-to-body is bound to the flat
				// heading-xs (10px at every tier), body-to-cta to stack-s (10px mobile
				// / 15px desktop). Image-to-caption is stack-m.
				<div className="mt-stack-m flex flex-col items-start gap-y-heading-xs">
					{slide.title && (
						<p className="text-label line-clamp-2 text-text-heading">
							<BevSafeText>{slide.title}</BevSafeText>
						</p>
					)}
					{(slide.description || showCta) && (
						<div className="flex flex-col items-start gap-y-stack-s">
							{slide.description && (
								<p className="text-body3 line-clamp-3 text-left text-text-primary">
									<BevSafeText>{slide.description}</BevSafeText>
								</p>
							)}
							{showCta && (
								<AnchorLink
									href={slide.ctaHref ?? ''}
									intent="text"
									target={slide.ctaNewWindow ? '_blank' : undefined}
									rel={slide.ctaNewWindow ? 'noopener' : undefined}
								>
									{slide.ctaLabel}
								</AnchorLink>
							)}
						</div>
					)}
				</div>
			)}
		</li>
	);
}

export default function InlineCarousel({
	title,
	id,
	externalAnchorId,
	headingLevel = 'h2',
	description,
	ctaLabel,
	ctaHref,
	ctaNewWindow,
	slides,
	className,
}: InlineCarouselProps): ReactElement | null {
	const translate = useTranslations();
	const scrollRef = useRef<HTMLUListElement>(null);

	useDragToScroll(scrollRef);
	const [activeIndex, setActiveIndex] = useState(0);

	const looping = slides.length > 1;
	const CLONE_OFFSET = looping ? 1 : 0; // number of clones prepended to the DOM list
	const domSlides = looping ? [slides[slides.length - 1], ...slides, slides[0]] : slides;

	const scrollToDomRef = useRef<(domIndex: number, behavior?: ScrollBehavior) => void>(
		() => void 0
	);
	scrollToDomRef.current = (domIndex: number, behavior: ScrollBehavior = 'smooth') => {
		const el = scrollRef.current;
		if (!el) return;
		const child = el.children[domIndex] as HTMLElement | undefined;
		if (!child) return;
		const target = child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2;
		el.scrollTo({ left: Math.max(0, target), behavior });
	};

	const measure = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		const center = el.scrollLeft + el.clientWidth / 2;
		let domNearest = 0;
		let min = Infinity;
		Array.from(el.children).forEach((child, index) => {
			const node = child as HTMLElement;
			const distance = Math.abs(node.offsetLeft + node.offsetWidth / 2 - center);
			if (distance < min) {
				min = distance;
				domNearest = index;
			}
		});
		const logical = Math.max(0, Math.min(domNearest - CLONE_OFFSET, slides.length - 1));
		setActiveIndex(logical);
	}, [CLONE_OFFSET, slides.length]);

	useEffect(() => {
		if (!looping) return;
		scrollToDomRef.current(CLONE_OFFSET, 'instant' as ScrollBehavior);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally mount-only

	useEffect(() => {
		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [measure, slides.length]);

	useEffect(() => {
		if (!looping) return;
		const el = scrollRef.current;
		if (!el) return;

		let debounceTimer: ReturnType<typeof setTimeout>;

		const handleScrollEnd = () => {
			const center = el.scrollLeft + el.clientWidth / 2;
			let domNearest = 0;
			let min = Infinity;
			Array.from(el.children).forEach((child, i) => {
				const node = child as HTMLElement;
				const d = Math.abs(node.offsetLeft + node.offsetWidth / 2 - center);
				if (d < min) {
					min = d;
					domNearest = i;
				}
			});
			// Jump by the clone-to-twin distance rather than re-centring the twin: `scrollend` also
			// fires mid-drag, and re-centring there would shift the cards under a held mouse by up to
			// half a card. Once the track has snapped the two are the same position.
			const jumpToTwin = (cloneIndex: number, twinIndex: number) => {
				const clone = el.children[cloneIndex] as HTMLElement;
				const twin = el.children[twinIndex] as HTMLElement;
				el.scrollTo({
					left: el.scrollLeft + twin.offsetLeft - clone.offsetLeft,
					behavior: 'instant' as ScrollBehavior,
				});
			};
			if (domNearest === 0) {
				jumpToTwin(0, slides.length);
			} else if (domNearest === slides.length + 1) {
				jumpToTwin(slides.length + 1, 1);
			}
		};

		const onScroll = () => {
			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(handleScrollEnd, 150);
		};

		const onScrollEnd = () => {
			clearTimeout(debounceTimer);
			handleScrollEnd();
		};
		el.addEventListener('scrollend', onScrollEnd);
		el.addEventListener('scroll', onScroll);

		return () => {
			el.removeEventListener('scrollend', onScrollEnd);
			el.removeEventListener('scroll', onScroll);
			clearTimeout(debounceTimer);
		};
	}, [looping, slides.length]);

	const scrollToIndex = (logicalIndex: number) => {
		scrollToDomRef.current(logicalIndex + CLONE_OFFSET, 'smooth');
	};

	if (slides.length === 0) return null;

	const showHeaderCta = Boolean(ctaLabel && ctaHref);

	const canScrollPrev = looping || activeIndex > 0;
	const canScrollNext = looping || activeIndex < slides.length - 1;

	const handlePrev = () => {
		if (looping && activeIndex === 0) {
			scrollToDomRef.current(0, 'smooth');
		} else {
			scrollToIndex(activeIndex - 1);
		}
	};

	const handleNext = () => {
		if (looping && activeIndex === slides.length - 1) {
			scrollToDomRef.current(slides.length + 1, 'smooth');
		} else {
			scrollToIndex(activeIndex + 1);
		}
	};

	return (
		<section
			className={cn(
				// Header-block-to-track gap: Figma "autolayout" itemSpacing is
				// 20px mobile / 30px desktop — exact match for stack-l.
				// The section stays uncapped so the track can span it edge to edge;
				// only the header sits on the wide grid (decision b96ff0, IAI-329).
				// The track keeps its full width, so the --card-w:...cqw calc below
				// resolves against the same box as before.
				'flex w-full flex-col gap-y-stack-l',
				className
			)}
		>
			{/* Figma has two gaps here, not one: title-to-body is heading-s (15px
			    mobile / 20px desktop), body-to-cta is stack-l (20px mobile / 30px
			    desktop) — they used to collapse into one flat gap-200 (20px),
			    matching neither tier correctly. */}
			<div className="flex w-full flex-col items-center gap-y-heading-s text-center spacing-wide-grid">
				{title && (
					<RichTextTitle
						title={title}
						headingLevel={headingLevel}
						id={externalAnchorId ?? id}
						className="text-h2 line-clamp-2 text-text-heading"
					/>
				)}
				{(description || showHeaderCta) && (
					<div className="flex flex-col items-center gap-y-stack-l">
						{description && (
							<p className="text-body1 text-text-primary md:max-w-[723px]">
								<BevSafeText>{description}</BevSafeText>
							</p>
						)}
						{showHeaderCta && (
							<AnchorLink
								href={ctaHref ?? ''}
								intent="filled"
								target={ctaNewWindow ? '_blank' : undefined}
								rel={ctaNewWindow ? 'noopener' : undefined}
							>
								{ctaLabel}
							</AnchorLink>
						)}
					</div>
				)}
			</div>

			<div className="group relative [--card-w:min(1044px,76cqw)]">
				<ul
					ref={scrollRef}
					onScroll={measure}
					className="no-scrollbar flex snap-x snap-mandatory gap-x-grid-gutter overflow-x-auto scroll-smooth px-[calc((100cqw_-_var(--card-w))/2)]"
					style={{ scrollbarWidth: 'none' }}
				>
					{domSlides.map((slide, domIndex) => {
						const isClone = looping && (domIndex === 0 || domIndex === domSlides.length - 1);
						const logicalIndex = domIndex - CLONE_OFFSET;
						const isActive = !isClone && logicalIndex === activeIndex;
						const key = isClone
							? domIndex === 0
								? `clone-last-${slide.id}`
								: `clone-first-${slide.id}`
							: slide.id;
						return <InlineCarouselCard key={key} slide={slide} isActive={isActive} />;
					})}
				</ul>

				<div className="pointer-events-none absolute inset-x-0 top-0 z-20 hidden spacing-wide-grid-inset items-center justify-between opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:flex md:h-[calc(var(--card-w)*9/16)]">
					<button
						type="button"
						onClick={handlePrev}
						aria-label={translate('Previous slide')}
						className={cn(
							arrowButtonClass,
							'ml-inline-xs',
							canScrollPrev ? 'pointer-events-auto' : 'invisible'
						)}
					>
						<IconChevronLightLeft width={20} height={20} />
					</button>
					<button
						type="button"
						onClick={handleNext}
						aria-label={translate('Next slide')}
						className={cn(
							arrowButtonClass,
							'mr-inline-xs',
							canScrollNext ? 'pointer-events-auto' : 'invisible'
						)}
					>
						<IconChevronLightRight width={20} height={20} />
					</button>
				</div>
			</div>
		</section>
	);
}
